import { WallData } from '@/editor/data/wall/Wall';
import { arcThresholdValue, thresholdValue } from '@/editor/math/const';
import { IPoint2d } from '@/editor/math/IPoint2d';
import WallStore from '@/editor/stores/WallStore';
import { modelManager } from '@/editor/manager/entityManager';
import { constructRectRoomWalls } from '@/editor/utils/room';
import { getUniqueId } from '@/editor/utils';
import { globalStores, TGlobalStores } from '@/editor/stores';
import { initDrawWallInfo } from '@/editor/data/wall/utils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { WallBaseData } from '@/editor/data/wall/wallBaseData';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import RoomStore from '@/editor/stores/RoomStore';
import SeparateWallStore from '@/editor/stores/SeparateWallStore';
import { EOperateMode, EWallOperateType, IOriginModifyWallInfo } from '../../types';
import { confirm } from '../../utils/feedback';
import i18n from '@/i18n';
import {
  EWallModifyType,
  IWallModifyUpdateItem,
  IWallModifyDelItem,
  IWallModifyAddItem,
  EOperationType
} from '@/editor/types/operation';
import { WallMoveCalculator } from '../../utils/wall/wallMoveCalculator';
import { SeparateWallData } from '@/editor/data/separateWall/SeparateWall';
import { IWindowData } from '@/editor/data/model/model';
import { EPointAndClosedRelation, IShapeSeg2d } from '@/editor/types/shape';
import { getPathFromEdges } from '@/editor/utils/edge';
import { getShapeSeg, subtractSegments } from '@/editor/math/shape/other';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { getPointAndClosedRelation } from '@/editor/math/shape/path';
import { FloatingPoint } from '@/editor/math/number';
import {
  MAX_CHORD_LENGTH,
  MAX_SAGITTA,
  MAX_WALL_LENGTH,
  MIN_CHORD_LENGTH,
  MIN_SAGITTA,
  MIN_WALL_LENGTH
} from '@/editor/constant/wall';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { getRoomRelateWallIds } from '@/editor/data/room/utils';

/** 删除项只用 `wallId`，不带墙体数据；新增与更新项都必须带上完整数据 */
type IModifyItem =
  | { wallId: string; type: EWallModifyType.Add | EWallModifyType.Update; wallData: WallData }
  | { wallId: string; type: EWallModifyType.Del };

interface IAddWallParams {
  start: IPoint2d;
  end: IPoint2d;
  operateMode: EOperateMode.DrawWall;
}

interface IAddRectWallParams extends Omit<IAddWallParams, 'operateMode'> {
  operateMode: EOperateMode.DrawRectWall;
}

interface IAddArcWallParams extends Omit<IAddWallParams, 'operateMode'> {
  operateMode: EOperateMode.DrawArcWall;
  center: IPoint2d;
  clockwise: boolean;
}

type TAddWallPrams = IAddWallParams | IAddRectWallParams | IAddArcWallParams;

export class WallOperation {
  private _wallStore: WallStore;
  private _separateWallStore: SeparateWallStore;
  private _roomStore: RoomStore;
  private _wallPan: WallMoveCalculator;

  private _gs: TGlobalStores;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    this._wallStore = gs.wallStore;
    this._separateWallStore = gs.separateWallStore;
    this._roomStore = gs.roomStore;

    this._wallPan = new WallMoveCalculator(this._wallStore);
  }

  get globalWallDatas() {
    return this._wallStore.getWallDatas();
  }

  get globalSeparateWallDatas() {
    return this._separateWallStore.separateWallDatas;
  }

  get globalRoomDatas() {
    return this._roomStore.getRoomDatas();
  }

  get wallPan() {
    return this._wallPan;
  }

  private $history_addWall(wallDatas: WallData[], removeSeparateWallDatas: SeparateWallData[]) {
    this._gs.history.startOperation();

    // 删除隔墙数据
    removeSeparateWallDatas.forEach((separateWallData) => {
      this._separateWallStore.removeSepData(separateWallData.id, false);
    });

    // 删除内嵌元素数据
    removeSeparateWallDatas
      .flatMap((swd) => this._gs.modelStore.getWindowsByWallId(swd.id))
      .forEach((windowData) => {
        this._gs.modelStore.removeWindowData(windowData.id);
      });

    // 添加墙数据
    wallDatas.forEach((wallData) => {
      this._wallStore.addWallData(wallData);
    });

    this._gs.refreshData();

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.WallAdd);
  }

  private $history_delWall(wallId: string, removeSeparateWallDatas: SeparateWallData[]) {
    this._gs.history.startOperation();

    const wallData = this._gs.wallStore.getWallData(wallId);
    if (!wallData) return;

    const relativeInsertElements: IWindowData[] = [];
    this._gs.modelStore.getWindowsByWallId(wallData.id).forEach((windowData) => {
      relativeInsertElements.push(windowData);
    });
    removeSeparateWallDatas.forEach((separateWallData) => {
      this._gs.modelStore.getWindowsByWallId(separateWallData.id)?.forEach((windowData) => {
        relativeInsertElements.push(windowData);
      });
    });
    relativeInsertElements.forEach((windowData) => {
      this._gs.modelStore.removeWindowData(windowData.id);
    });

    removeSeparateWallDatas.forEach((separateWallData) => {
      this._gs.separateWallStore.removeSepData(separateWallData.id, false);
    });

    this._gs.wallStore.removeWallDataById(wallData.id);

    this._gs.refreshData();

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.WallDel);
  }

  private $history_modifyWall(
    updateWallDatas: (IWallModifyUpdateItem | IWallModifyAddItem | IWallModifyDelItem)[],
    removeSeparateWallDatas: SeparateWallData[]
  ) {
    this._gs.history.startOperation();

    const removeInsertElementDatas: IWindowData[] = [];

    removeSeparateWallDatas.forEach((item) => {
      this._gs.modelStore.getWindowsByWallId(item.id).forEach((windowData) => {
        removeInsertElementDatas.push(windowData);
      });
    });

    const updateList: IModifyItem[] = updateWallDatas
      .map((item): IModifyItem | null => {
        if (item.type === EWallModifyType.Del) {
          this._gs.modelStore.getWindowsByWallId(item.wallId).forEach((windowData) => {
            removeInsertElementDatas.push(windowData);
          });
          return { wallId: item.wallId, type: item.type };
        }

        const oldWallData = this._gs.wallStore.getWallData(item.wallId);
        if (oldWallData) {
          return { wallId: item.wallId, type: item.type, wallData: Object.assign({}, oldWallData, item.newWallData) };
        }

        // store 里没有该墙体：新增项自带完整数据；更新项缺少底数无法拼出完整墙体，只能跳过
        if (item.type === EWallModifyType.Add)
          return { wallId: item.wallId, type: item.type, wallData: item.newWallData };
        return null;
      })
      .filter((item) => item !== null);

    removeInsertElementDatas.forEach((item) => {
      this._gs.modelStore.removeWindowData(item.id);
    });

    removeSeparateWallDatas.forEach((item) => {
      this._gs.separateWallStore.removeSepData(item.id, false);
    });

    // TODO: 待优化，此处会进行多次轮廓计算，优化为一次计算
    updateList.forEach((item) => {
      switch (item.type) {
        case EWallModifyType.Update:
          this._gs.updateWallData(item.wallId, item.wallData.baseData);
          break;
        case EWallModifyType.Add:
          this._gs.wallStore.addWallData(item.wallData);
          break;
        case EWallModifyType.Del:
          this._gs.wallStore.removeWallDataById(item.wallId);
          break;
      }
    });

    this._gs.refreshData();

    // 将操作记录压栈
    this._gs.history.completeOperation(EOperationType.WallModify);
  }

  async confirmRemoveSeparateWall() {
    return await confirm({
      content: i18n.t('partition.info.existpartition')
    });
  }

  async addWall(params: TAddWallPrams) {
    const { start, end, operateMode } = params;

    // 检查隔墙房间
    let segs: IShapeSeg2d[];
    if (operateMode === EOperateMode.DrawRectWall) {
      const points = constructRectRoomWalls(start, end);
      segs = points.map((point, index) => new LineSeg2d(point, points[(index + 1) % points.length]));
    } else if (operateMode === EOperateMode.DrawArcWall) {
      segs = [getShapeSeg({ start, end, center: params.center, clockwise: params.clockwise })];
    } else {
      segs = [new LineSeg2d(start, end)];
    }

    const infectedRoomIds = this.getInfectedRoomIds(segs);
    if (infectedRoomIds.length) {
      const isConfirmed = await this.confirmRemoveSeparateWall();
      if (!isConfirmed) return;
    }

    let isAddSuccess;
    if (operateMode === EOperateMode.DrawRectWall) {
      isAddSuccess = !!this.addRectWall(start, end, infectedRoomIds);
    } else if (operateMode === EOperateMode.DrawArcWall) {
      // TODO: 待实现
      isAddSuccess = !!this.addArcWall({
        start,
        end,
        center: params.center,
        clockwise: params.clockwise,
        crossRoomIds: infectedRoomIds
      });
    } else {
      isAddSuccess = !!this.addSingleWall(start, end, infectedRoomIds);
    }

    return isAddSuccess;
  }

  async removeWall(wallId: string) {
    const wd = this._wallStore.getWallData(wallId);
    if (!wd) return;

    const infectedRoomIds = this.getInfectedRoomIds([getShapeSeg(wd.baseData)]);

    if (infectedRoomIds.length) {
      const isConfirmed = await this.confirmRemoveSeparateWall();
      if (!isConfirmed) return;
    }

    const removeSeparateWallDatas = this._separateWallStore.getSepDatas({
      roomIds: infectedRoomIds,
      onlyNotBase: true
    });

    this.$history_delWall(wallId, removeSeparateWallDatas);
    modelManager.updateEntity();
  }

  private addRectWall(start: IPoint2d, end: IPoint2d, crossRoomIds: string[]) {
    if (!this.isLegalRectWallSize(start, end)) return;

    const { wallThickness, layerHeight, offGround } = globalStores.paramStore;

    const points = constructRectRoomWalls(start, end);
    const len = points.length;
    const baseWallDatas: WallBaseData[] = [];
    for (let i = 0; i < len; i++) {
      const rectStart = points[i];
      const rectEnd = points[(i + 1) % len];
      baseWallDatas.push({
        start: rectStart,
        end: rectEnd,
        thickness: wallThickness,
        height: layerHeight,
        offGround
      });
    }

    const addedIds = this._addWalls(baseWallDatas, crossRoomIds);
    modelManager.updateEntity();

    return addedIds;
  }

  private addSingleWall(start: IPoint2d, end: IPoint2d, crossRoomIds: string[]) {
    if (!FloatingPoint.between(Point2dUtils.distanceTo(start, end), MIN_WALL_LENGTH, MAX_WALL_LENGTH)) return;

    const { wallThickness, layerHeight, offGround } = globalStores.paramStore;
    const addedIds = this._addWalls(
      [{ start, end, thickness: wallThickness, height: layerHeight, offGround }],
      crossRoomIds
    );
    modelManager.updateEntity();

    return addedIds;
  }

  private addArcWall(params: {
    start: IPoint2d;
    end: IPoint2d;
    center: IPoint2d;
    clockwise: boolean;
    crossRoomIds: string[];
  }) {
    const { start, end, center, clockwise, crossRoomIds } = params;
    const sagitta = new ArcSeg2d({ start, end, center, clockwise }).getSagitta();

    // 弦高/弦长不在允许范围内或者已有中心点一样的弧墙，则不允许添加
    if (
      !FloatingPoint.between(Point2dUtils.distanceTo(start, end), MIN_CHORD_LENGTH, MAX_CHORD_LENGTH) ||
      !FloatingPoint.between(Math.abs(sagitta), MIN_SAGITTA, MAX_SAGITTA) ||
      this._hasSameArcWall({ start, end, center, clockwise })
    ) {
      return;
    }

    const { wallThickness, layerHeight, offGround } = globalStores.paramStore;
    const addedIds = this._addWalls(
      [{ start, end, center, clockwise, thickness: wallThickness, height: layerHeight, offGround }],
      crossRoomIds
    );
    modelManager.updateEntity();

    return addedIds;
  }

  /** 判断是否已有一样的弧形墙 */
  private _hasSameArcWall(params: { start: IPoint2d; end: IPoint2d; center: IPoint2d; clockwise: boolean }) {
    const { start, end, center, clockwise } = params;

    return this.globalWallDatas
      .filter(({ baseData }) => baseData.center !== undefined)
      .some(({ baseData }) => {
        if (!baseData.center || !Point2dUtils.equals(baseData.center, center, arcThresholdValue)) return false;

        const isSameDirectionArc =
          Point2dUtils.equals(baseData.start, start, arcThresholdValue) &&
          Point2dUtils.equals(baseData.end, end, arcThresholdValue) &&
          baseData.clockwise === clockwise;

        if (isSameDirectionArc) return true;

        const isOppositeDirectionArc =
          Point2dUtils.equals(baseData.start, end, arcThresholdValue) &&
          Point2dUtils.equals(baseData.end, start, arcThresholdValue) &&
          baseData.clockwise === !clockwise;

        if (isOppositeDirectionArc) return true;

        return false;
      });
  }

  /* @return 新增墙体id数组/undefined */
  private _addWalls(baseWallDatas: WallBaseData[], crossRoomIds: string[]) {
    const wallLength = baseWallDatas.length;
    if (!wallLength) return;

    const globalSegs = this._wallStore.getGeometricInfos().map((info) => info.seg);
    // 分割后的墙体
    const splitWallDatas: WallData[] = [];
    baseWallDatas.forEach((baseWallData) => {
      const seg = getShapeSeg(baseWallData);
      // 获取重叠部份；
      const overlapInfo = this.getOverlapSegs(seg, globalSegs);
      // 减去重叠部份；
      const splitSegs = subtractSegments(seg, overlapInfo.overlapSegs);

      splitSegs.forEach((seg) => {
        const baseData: WallBaseData = {
          start: Point2dUtils.equals(baseWallData.start, seg.start) ? baseWallData.start : seg.start,
          end: Point2dUtils.equals(baseWallData.end, seg.end) ? baseWallData.end : seg.end,
          thickness: baseWallData.thickness,
          height: baseWallData.height,
          offGround: baseWallData.offGround
        };
        if (baseWallData.center) {
          baseData.center = baseWallData.center;
          baseData.clockwise = baseWallData.clockwise;
        }

        const drawWallInfo = initDrawWallInfo(baseData);
        splitWallDatas.push({
          id: getUniqueId(),
          baseData,
          drawWallInfo
        });
      });
    });

    if (!splitWallDatas.length) return;

    const removeSeparateWallDatas = this._separateWallStore.getSepDatas({
      roomIds: crossRoomIds,
      onlyNotBase: true
    });

    this.$history_addWall(splitWallDatas, removeSeparateWallDatas);

    return splitWallDatas.map((wd) => wd.id);
  }

  private getRoomDatasWithSeparateWall() {
    const roomIdsWithSeparateWall = new Set();
    this.globalSeparateWallDatas.forEach((separateWallData) => {
      if (separateWallData.relateRoomId) {
        roomIdsWithSeparateWall.add(separateWallData.relateRoomId);
      }
    });
    if (roomIdsWithSeparateWall.size === 0) return [];

    return this.globalRoomDatas.filter((roomData) => roomIdsWithSeparateWall.has(roomData.id));
  }

  // TODO: 待优化
  // 获取墙体与房间的交集
  private getInfectedRoomIds(segs: IShapeSeg2d[]) {
    const roomDatasWithSeparateWall = this.getRoomDatasWithSeparateWall();
    const intersectRoomIds: string[][] = [];
    const includeRoomIds: string[] = [];
    const borderRoomIds: string[][] = [];

    // --------------- 获取所有房间的线段 ---------------
    const roomSegs = roomDatasWithSeparateWall.map((roomData) => {
      const relateWallSegs = getRoomRelateWallIds(roomData).flatMap((id) => {
        // 房间关联的墙 id 来自派生数据，正常都能在 store 里取到；取不到就跳过，不让空数据进入比对
        const wallData = this._wallStore.getWallData(id);
        return wallData ? [getShapeSeg(wallData.baseData)] : [];
      });

      // 内轮廓
      const segs: IShapeSeg2d[] = roomData.pathInfos.map((info) => getShapeSeg(info));
      return { segs, id: roomData.id, relateWallSegs };
    });

    // --------------- 获取存在相交线段的房间 ---------------
    segs.forEach((seg, index) => {
      intersectRoomIds[index] = [];
      for (const { segs: _segs, id } of roomSegs) {
        for (const _seg of _segs) {
          const intersect = intersectCommonShape(seg, _seg);
          if (!intersect) continue;
          intersectRoomIds[index].push(id);
          break;
        }
      }
    });

    // --------------- 获取包含线段的房间 ---------------
    segs.forEach((seg, index) => {
      for (const roomData of roomDatasWithSeparateWall) {
        const path = getPathFromEdges(roomData.pathInfos);
        if (
          getPointAndClosedRelation(seg.start, path) === EPointAndClosedRelation.Inside &&
          getPointAndClosedRelation(seg.end, path) === EPointAndClosedRelation.Inside
        ) {
          includeRoomIds[index] = roomData.id;
          break;
        }
      }
    });

    // --------------- 房间组成墙体中包含入参线段的房间 ---------------
    segs.forEach((seg, index) => {
      borderRoomIds[index] = [];
      for (const room of roomSegs) {
        for (const wallSeg of room.relateWallSegs) {
          if (Point2dUtils.equals(seg.start, wallSeg.start) && Point2dUtils.equals(seg.end, wallSeg.end)) {
            borderRoomIds[index].push(room.id);
            break;
          }
        }
      }
    });

    const infectedRoomIds = Array.from(
      new Set([...intersectRoomIds.flat(), ...includeRoomIds, ...borderRoomIds.flat()])
    );
    return infectedRoomIds;
  }

  private isLegalRectWallSize(start: IPoint2d, end: IPoint2d) {
    if (
      FloatingPoint.between(Math.abs(start.x - end.x), MIN_WALL_LENGTH, MAX_WALL_LENGTH) &&
      FloatingPoint.between(Math.abs(start.y - end.y), MIN_WALL_LENGTH, MAX_WALL_LENGTH)
    ) {
      return true;
    }

    return false;
  }

  // TODO: 有问题
  // seg: 需要分割的线段，referenceSegs: 参考线段
  private getOverlapSegs(seg: IShapeSeg2d, referenceSegs: IShapeSeg2d[]) {
    const overlapSegs: IShapeSeg2d[] = [];
    const overlapIndexs: number[] = [];

    referenceSegs.forEach((globalSegs, index) => {
      if (seg instanceof LineSeg2d && globalSegs instanceof LineSeg2d && seg.isCollinear(globalSegs)) {
        const overlapSeg = seg.getOverlapSegment(globalSegs);
        // if (overlapSeg && overlapSeg.length > OVERLAPLENGTH) {
        if (overlapSeg) {
          overlapIndexs.push(index);
          overlapSegs.push(overlapSeg);
        }
      }

      // TODO: 待补充
    });

    return { overlapIndexs, overlapSegs };
  }

  async moveWallPoint(
    wallId: string,
    operateData: {
      type: EWallOperateType.StartPoint | EWallOperateType.EndPoint | EWallOperateType.ArcMidPoint;
      point: IPoint2d;
    }
  ) {
    this._wallPan.setMainWall(wallId);
    const { modifyWallInfos } = this._wallPan.compute(operateData);

    const isSuccess = await this._modifyExecute(modifyWallInfos);
    return isSuccess;
  }

  async modifyWallBaseData(wallId: string, baseData: Partial<WallBaseData>) {
    const wallData = this._wallStore.getWallData(wallId);
    if (!wallData) return;

    await this._modifyExecute([{ wallId, updateBaseData: baseData }]);
  }

  // 往墙体方向左侧移动为正
  async pan(wallId: string, offset: number, direction?: IPoint2d) {
    const wallData = this._wallStore.getWallData(wallId);
    if (!wallData) return false;

    this._wallPan.setMainWall(wallId);
    const result = this._wallPan.compute({ type: EWallOperateType.Pan, offset, direction });
    if (!result) return false;

    const isSuccess = await this._modifyExecute(result.modifyWallInfos);
    return isSuccess;
  }

  // 暂不支持有重叠的墙体数据入参
  private async _modifyExecute(modifyWallInfos?: IOriginModifyWallInfo[]) {
    if (!modifyWallInfos) return false;

    // ------------------------- 先判断要修改的墙体是否属于间隔房间 --------------------------------
    // 入参里的墙体全部先取出来：任一取不到说明入参与 store 不一致，此时尚未开启 history 事务，整体放弃比改一半安全
    const modifyItems: { modifyInfo: IOriginModifyWallInfo; wallData: WallData }[] = [];
    for (const modifyInfo of modifyWallInfos) {
      const wallData = this._wallStore.getWallData(modifyInfo.wallId);
      if (!wallData) return false;
      modifyItems.push({ modifyInfo, wallData });
    }

    const modifyOldSegs = modifyItems.map(({ wallData }) => getShapeSeg(wallData.baseData));
    let infectedRoomIds = this.getInfectedRoomIds(modifyOldSegs);

    let removeSeparateWallDatas = this._separateWallStore.getSepDatas({
      roomIds: infectedRoomIds,
      onlyNotBase: true
    });
    let isConfirmed = false;
    if (removeSeparateWallDatas.length) {
      isConfirmed = await this.confirmRemoveSeparateWall();
      if (!isConfirmed) return false;
    }

    // ------------------------- 拆分墙，需要和其他墙体进行共线判断  --------------------------------
    const _commandModifyWallInfos: (IWallModifyUpdateItem | IWallModifyAddItem | IWallModifyDelItem)[] = [];

    const filterIds = modifyWallInfos.map((info) => info.wallId);
    const referenceInfos = this._wallStore.getGeometricInfos().filter((info) => !filterIds.includes(info.id));
    modifyItems.forEach(({ modifyInfo, wallData }) => {
      const baseData = { ...wallData.baseData, ...modifyInfo.updateBaseData };
      const seg = getShapeSeg(baseData);
      const overlapResult = this.getOverlapSegs(
        seg,
        referenceInfos.map((info) => info.seg)
      );
      const splitSegs = subtractSegments(seg, overlapResult.overlapSegs).filter((seg) => seg.length > thresholdValue);
      if (splitSegs.length === 1) {
        _commandModifyWallInfos.push({
          wallId: modifyInfo.wallId, // 只分割出一条时，保留原id
          type: EWallModifyType.Update,
          newWallData: {
            ...wallData,
            baseData: {
              ...baseData,
              start: splitSegs[0].start,
              end: splitSegs[0].end
            }
          }
        });
      } else if (splitSegs.length > 1) {
        // 被分割出多条的话，删除原墙体，新增多条墙体
        _commandModifyWallInfos.push({
          wallId: modifyInfo.wallId,
          type: EWallModifyType.Del,
          newWallData: undefined
        });

        splitSegs.forEach((seg) => {
          const _baseData = {
            ...baseData,
            start: seg.start,
            end: seg.end
          };
          const drawWallInfo = initDrawWallInfo(_baseData);

          const newWallData = {
            id: getUniqueId(),
            baseData: _baseData,
            drawWallInfo
          };
          _commandModifyWallInfos.push({
            wallId: newWallData.id,
            type: EWallModifyType.Add,
            newWallData
          });
        });
      } else {
        _commandModifyWallInfos.push({
          wallId: modifyInfo.wallId,
          type: EWallModifyType.Del,
          newWallData: undefined
        });
      }
    });

    // ------------------------- 再次判断拆分后的墙体是否与间隔房间有交集 --------------------------------
    const segs = _commandModifyWallInfos
      .filter((info) => info.type === EWallModifyType.Add || info.type === EWallModifyType.Update)
      .map((info) => {
        const baseData = info.newWallData.baseData;
        if (!baseData?.start || !baseData?.end) return undefined;
        return getShapeSeg(baseData);
      })
      .filter((seg) => seg !== undefined);
    // 重新计算要移动的隔墙
    infectedRoomIds.push(...this.getInfectedRoomIds(segs));
    infectedRoomIds = Array.from(new Set(infectedRoomIds));
    removeSeparateWallDatas = this._separateWallStore.getSepDatas({
      roomIds: infectedRoomIds,
      onlyNotBase: true
    });
    if (removeSeparateWallDatas.length && !isConfirmed) {
      isConfirmed = await this.confirmRemoveSeparateWall();
      if (!isConfirmed) return false;
    }

    // ------------------------- 执行修改 --------------------------------
    this.$history_modifyWall(_commandModifyWallInfos, removeSeparateWallDatas);

    modelManager.updateEntity();

    return true;
  }
}
